import { Box, Stack, Typography, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getColors, shadows } from "./payments.styles";
import CircleIcon from "@mui/icons-material/Circle";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useState } from "react";

const RevenueByService = ({ data = [] }) => {
  const theme = useTheme();
  const colors = getColors(theme);
  const primaryColor = theme.palette.primary.main;
  const [anchorEl, setAnchorEl] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const open = Boolean(anchorEl);

  const chartData = data;

  const total = chartData.reduce((sum, item) => sum + item.amount, 0);
  const formattedTotal = `₹${(total / 100000).toFixed(1)}L`;

  const chartColors = [
    primaryColor,          
    colors.chartPurple,     
    colors.chartGreen,      
  ];

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: 3,
        p: 3,
        border: `1px solid ${colors.borderLight}`,
        boxShadow: shadows.card,
        height: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 700,
            color: colors.textPrimary,
          }}
        >
          Payment by Service
        </Typography>
        {/* <IconButton
          size="small"
          onClick={handleMenuClick}
          sx={{
            color: colors.textMuted,
            "&:hover": {
              bgcolor: colors.bgPrimary,
            },
          }}
        >
          <MoreVertIcon sx={{ fontSize: 18 }} />
        </IconButton> */}
        {/* <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: shadows.dropdown,
              minWidth: 150,
            },
          }}
        >
          <MenuItem onClick={handleMenuClose} sx={{ fontSize: 13 }}>View Details</MenuItem>
          <MenuItem onClick={handleMenuClose} sx={{ fontSize: 13 }}>Export Data</MenuItem>
        </Menu> */}
      </Stack>

      {/* Donut Chart */}
      {chartData.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 280,
            color: colors.textMuted,
          }}
        >
          <Typography fontSize={14}>No service data available</Typography>
        </Box>
      ) : (
      <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mb: 3,
          position: "relative",
        }}
      >
        <svg width="200" height="200" viewBox="0 0 200 200">
          <defs>
            <linearGradient id="gradient-primary" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={primaryColor} />
              <stop offset="100%" stopColor={colors.chartPrimaryLight || primaryColor} />
            </linearGradient>
            <linearGradient id="gradient-purple" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={colors.chartPurple} />
              <stop offset="100%" stopColor="#A78BFA" />
            </linearGradient>
            <linearGradient id="gradient-green" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={colors.chartGreen} />
              <stop offset="100%" stopColor="#34D399" />
            </linearGradient>
          </defs>

          {/* Background circle */}
          <circle
            cx="100"
            cy="100"
            r="80"
            fill="none"
            stroke={colors.borderLight}
            strokeWidth="24"
          />

          {/* Segments */}
          {chartData.map((item, index) => {
            const startAngle = chartData
              .slice(0, index)
              .reduce((sum, d) => sum + (d.value * 360) / 100, 0);
            const angle = (item.value * 360) / 100;
            const endAngle = startAngle + angle;

            const startRad = ((startAngle - 90) * Math.PI) / 180;
            const endRad = ((endAngle - 90) * Math.PI) / 180;

            const x1 = 100 + 80 * Math.cos(startRad);
            const y1 = 100 + 80 * Math.sin(startRad);
            const x2 = 100 + 80 * Math.cos(endRad);
            const y2 = 100 + 80 * Math.sin(endRad);

            const largeArc = angle > 180 ? 1 : 0;

            const gradientId = index === 0 ? "gradient-primary" : index === 1 ? "gradient-purple" : "gradient-green";
            const formattedAmount = item.amount >= 100000
              ? `₹${(item.amount / 100000).toFixed(1)}L`
              : `₹${item.amount.toLocaleString()}`;

            return (
              <Tooltip
                key={index}
                title={
                  <Box sx={{ p: 0.5 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{item.name}</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{formattedAmount}</Typography>
                    <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>{item.value}% of total</Typography>
                  </Box>
                }
                arrow
                placement="top"
              >
                <path
                  d={`M 100 100 L ${x1} ${y1} A 80 80 0 ${largeArc} 1 ${x2} ${y2} Z`}
                  fill={`url(#${gradientId})`}
                  opacity={hoveredIndex === null || hoveredIndex === index ? "0.9" : "0.5"}
                  style={{ cursor: "pointer", transition: "opacity 0.2s" }}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                />
              </Tooltip>
            );
          })}

          {/* Inner white circle */}
          <circle cx="100" cy="100" r="56" fill="#FFFFFF" />

          {/* Center text */}
          <text
            x="100"
            y="95"
            textAnchor="middle"
            fill={colors.textPrimary}
            fontSize="28"
            fontWeight="700"
          >
            {formattedTotal}
          </text>
          <text
            x="100"
            y="115"
            textAnchor="middle"
            fill={colors.textMuted}
            fontSize="11"
            fontWeight="600"
          >
            TOTAL
          </text>
        </svg>
      </Box>

      {/* Legend */}
      <Stack direction="row" spacing={3} justifyContent="center" flexWrap="wrap">
        {chartData.map((item, index) => {
          const formattedAmount = item.amount >= 100000
            ? `₹${(item.amount / 100000).toFixed(1)}L`
            : `₹${item.amount.toLocaleString()}`;

          return (
            <Tooltip
              key={index}
              title={`${item.name}: ${formattedAmount}`}
              arrow
              placement="top"
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  cursor: "pointer",
                  opacity: hoveredIndex === null || hoveredIndex === index ? 1 : 0.5,
                  transition: "opacity 0.2s",
                  p: 0.5,
                  borderRadius: 1,
                  "&:hover": { bgcolor: colors.bgPrimary },
                }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <CircleIcon sx={{ fontSize: 10, color: chartColors[index] }} />
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: hoveredIndex === index ? 600 : 500,
                    color: colors.textSecondary,
                  }}
                >
                  {item.name} ({item.value}%)
                </Typography>
              </Stack>
            </Tooltip>
          );
        })}
      </Stack>
      </>
      )}
    </Box>
  );
};

export default RevenueByService;
